/**
 * ⚔️ ArenaMarketPanel — Battle Coins sink. Mounted on /arena.
 *
 * 4 sections by tier: 🩸 HP · 🔮 Boosts · 🎨 Cosmetics · 🥚 Premium
 * Weekly cap counters per item, sold-out greys out.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/confirm-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABEL = {
  gear:        { label: "⚔️ Weapons & Armor",      accent: "text-orange-300" },
  smith_stone: { label: "🪨 Smith — Luck Stones",   accent: "text-emerald-300" },
  smith_mat:   { label: "🪵 Smith — Raw Materials", accent: "text-emerald-300" },
  cavern_key:  { label: "🗝️ Cavern Keys (Chits)",   accent: "text-amber-300" },
  hp:          { label: "🩸 HP Items",              accent: "text-red-300" },
  boost:       { label: "🔮 Combat Boosts",         accent: "text-purple-300" },
  cosmetic:    { label: "🎨 Cosmetics",             accent: "text-pink-300" },
  egg:         { label: "🥚 Premium Eggs",          accent: "text-amber-300" },
};

function fmtCountdown(secs) {
  if (!secs || secs <= 0) return "now";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function ArenaMarketPanel() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/arena/market/state`);
      setState(r.data);
    } catch {
      // panel hides on error
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onBuy = async (item_id, item) => {
    if (busy) return;
    const isChits = item.currency === "chits";
    const isOd = item.currency === "omni_dollars";
    const price = isChits ? item.price_chits : isOd ? item.price_od : item.price_bc;
    const sym = isChits ? "🪙 Chits" : isOd ? "Ⓞ OD" : "🟡 Battle Coins";
    const ok = await confirmDialog({
      title: `Buy ${item.name}?`,
      message: `${item.icon || ""} ${item.name}
Cost: ${price.toLocaleString()} ${sym}.`,
      confirmLabel: `Buy — ${price.toLocaleString()} ${sym}`,
      cancelLabel: "Not yet",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/arena/market/buy`, { item_id, qty: 1 });
      const g = r.data.granted || {};
      if (g.eggs?.length) {
        toast.success(`🥚 ${item.name} acquired — rolled ${g.eggs.join(", ")}! (-${r.data.spent} 🟡)`, { duration: 6000 });
      } else {
        toast.success(`${item.icon} ${item.name} acquired! (-${r.data.spent} 🟡)`);
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Buy failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  const tiers = ["gear", "smith_stone", "smith_mat", "cavern_key", "hp", "boost", "cosmetic", "egg"];

  return (
    <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-[#1a1208] via-[#0A0A0A] to-[#1a0820] p-4 mb-5" data-testid="arena-market-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-lg font-black tracking-wide text-amber-300" style={{ fontFamily: 'Anton, sans-serif' }}>
            ⚔️ ARENA MARKET
          </div>
          <div className="text-xs text-neutral-400">
            Spend 🟡 Battle Coins · Weekly caps reset in <span className="text-amber-300 font-bold">{fmtCountdown(state.seconds_to_reset)}</span>
          </div>
        </div>
        <div className="px-3 py-1.5 rounded-full bg-black/40 border border-amber-500/40 text-sm flex items-center gap-2">
          <span>🟡</span>
          <span className="font-bold text-amber-300" data-testid="arena-market-bc">{state.battle_coins}</span>
          <span className="text-neutral-500 text-xs">Battle Coins</span>
        </div>
      </div>

      <div className="space-y-4">
        {tiers.map((tier) => {
          const items = state.items.filter((it) => it.tier === tier);
          if (!items.length) return null;
          const t = TIER_LABEL[tier];
          return (
            <div key={tier}>
              <div className={`text-xs uppercase tracking-wider font-bold ${t.accent} mb-2`}>
                {t.label}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {items.map((it) => {
                  const isChits = it.currency === "chits";
                  const isOd    = it.currency === "omni_dollars";
                  const price = isChits ? it.price_chits : isOd ? it.price_od : it.price_bc;
                  const wallet = isChits ? (state.chits || 0) : isOd ? (state.omni_dollars || 0) : state.battle_coins;
                  const canAfford = wallet >= price;
                  const soldOut = it.sold_out;
                  const disabled = busy || !canAfford || soldOut;
                  const sym = isChits ? "🪙" : isOd ? "Ⓞ" : "🟡";
                  return (
                    <div key={it.item_id}
                         className={`rounded-lg border p-2 text-center ${
                           soldOut ? "border-neutral-800 bg-black/30 opacity-50" :
                           isChits ? "border-amber-500/40 bg-gradient-to-br from-amber-950/30 to-black/40" :
                           isOd ? "border-fuchsia-500/40 bg-gradient-to-br from-fuchsia-950/30 to-black/40" :
                           "border-neutral-700 bg-black/40"
                         }`}
                         data-testid={`arena-market-item-${it.item_id}`}>
                      <div className="text-3xl">{it.icon}</div>
                      <div className="font-bold text-xs mt-1 truncate">{it.name}</div>
                      <div className="text-[10px] text-neutral-500 truncate">{it.description}</div>
                      {it.weekly_cap != null && (
                        <div className="text-[10px] text-amber-300 mt-1">
                          {it.bought_this_week}/{it.weekly_cap} this week
                        </div>
                      )}
                      <button data-testid={`arena-market-buy-${it.item_id}`}
                              disabled={disabled} onClick={() => onBuy(it.item_id, it)}
                              className={`w-full mt-1.5 px-2 py-1 rounded disabled:opacity-50 text-white text-xs font-bold ${
                                isChits ? "bg-amber-700 hover:bg-amber-600" :
                                isOd ? "bg-fuchsia-700 hover:bg-fuchsia-600" :
                                "bg-amber-600 hover:bg-amber-500"
                              }`}>
                        {soldOut ? "✅ Sold out" : `${sym} ${price}`}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
